import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Zap, RefreshCw, ShieldCheck, Plus, Trash2, Edit3,
  CheckCircle, XCircle, AlertCircle, DollarSign, TrendingUp, Users,
  ArrowUpRight, ArrowDownLeft, RotateCcw, Star, Crown, Package,
  ToggleLeft, ToggleRight, Copy, Check, Eye, Filter, Search,
  ChevronDown, ChevronUp, Sparkles, Clock, BadgeCheck, X
} from 'lucide-react';
import * as api from '../../api';

/* ─── tiny badge helper ─── */
const StatusPill = ({ s }) => {
  const map = {
    paid:      { bg: 'rgba(16,185,129,.18)', c: '#10B981', label: 'PAID' },
    created:   { bg: 'rgba(245,158,11,.18)', c: '#F59E0B', label: 'PENDING' },
    failed:    { bg: 'rgba(239,68,68,.18)',  c: '#EF4444', label: 'FAILED' },
    refunded:  { bg: 'rgba(139,92,246,.18)', c: '#8B5CF6', label: 'REFUNDED' },
    active:    { bg: 'rgba(16,185,129,.18)', c: '#10B981', label: 'ACTIVE' },
    cancelled: { bg: 'rgba(239,68,68,.18)',  c: '#EF4444', label: 'CANCELLED' },
    expired:   { bg: 'rgba(107,114,128,.18)', c: '#6B7280', label: 'EXPIRED' },
  };
  const d = map[s] || map.created;
  return (
    <span style={{ fontSize: '0.68rem', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: d.bg, color: d.c, letterSpacing: '0.05em' }}>
      {d.label}
    </span>
  );
};

/* ─── stat card ─── */
const StatCard = ({ icon: Icon, label, value, sub, color = '#00FF87' }) => (
  <div className="cyber-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      <div style={{ width: 32, height: 32, borderRadius: '8px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={16} color={color} />
      </div>
    </div>
    <div style={{ fontSize: '1.7rem', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{sub}</div>}
  </div>
);

/* ─── plan card ─── */
const PlanCard = ({ plan, onEdit, onDelete, onToggle }) => (
  <div className="cyber-card" style={{
    padding: '20px', position: 'relative', overflow: 'hidden',
    border: plan.isActive ? '1px solid rgba(139,92,246,.4)' : '1px solid var(--border-color)',
    background: plan.isActive ? 'linear-gradient(135deg,rgba(139,92,246,.06),rgba(15,23,42,.7))' : 'rgba(15,23,42,.5)'
  }}>
    {plan.billingCycle === 'annual' && (
      <div style={{ position: 'absolute', top: 10, right: 10 }}>
        <span style={{ fontSize: '0.65rem', background: 'rgba(245,158,11,.2)', color: '#F59E0B', padding: '2px 7px', borderRadius: '999px', fontWeight: 800 }}>
          <Star size={9} style={{ display: 'inline', marginRight: 3 }} />BEST VALUE
        </span>
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <Crown size={16} color="var(--neon-purple)" />
      <span style={{ fontWeight: 900, fontSize: '1rem', color: 'var(--text-primary)' }}>{plan.name}</span>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--neon-purple)', marginBottom: '4px' }}>
      ₹{plan.price?.toLocaleString()}
      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 500 }}>/{plan.billingCycle}</span>
    </div>
    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>{plan.durationDays} days access</div>
    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {(plan.features || []).map((f, i) => (
        <li key={i} style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
          <CheckCircle size={11} color="#10B981" style={{ marginTop: 2, flexShrink: 0 }} />
          {f}
        </li>
      ))}
    </ul>
    <div style={{ display: 'flex', gap: '6px' }}>
      <button onClick={() => onToggle(plan)} style={{ flex: 1, padding: '7px 8px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', color: plan.isActive ? '#EF4444' : '#10B981', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {plan.isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
        {plan.isActive ? 'Deactivate' : 'Activate'}
      </button>
      <button onClick={() => onEdit(plan)} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(59,130,246,.4)', background: 'rgba(59,130,246,.1)', cursor: 'pointer', color: '#3B82F6' }}>
        <Edit3 size={13} />
      </button>
      <button onClick={() => onDelete(plan.id)} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,.4)', background: 'rgba(239,68,68,.1)', cursor: 'pointer', color: '#EF4444' }}>
        <Trash2 size={13} />
      </button>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function PaymentsView({ showTemporarySuccess = () => {} }) {
  // ── tabs ──────────────────────────────────────────────────
  const [tab, setTab] = useState('simulate');

  // ── gateway simulator ─────────────────────────────────────
  const [simAmount, setSimAmount] = useState('499');
  const [simFor, setSimFor] = useState('booking');
  const [simBookingId, setSimBookingId] = useState('');
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simError, setSimError] = useState('');

  // ── payment ledger ────────────────────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // ── refund ────────────────────────────────────────────────
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  // ── plans ─────────────────────────────────────────────────
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', price: '', billingCycle: 'monthly', durationDays: '30', features: '' });

  // ── subscriptions ─────────────────────────────────────────
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  // ── stats ─────────────────────────────────────────────────
  const [stats, setStats] = useState({ total: 0, paid: 0, refunded: 0, revenue: 0, activeSubs: 0 });

  // ── copied ────────────────────────────────────────────────
  const [copied, setCopied] = useState('');
  const copyText = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 2000); };

  /* ── loaders ──────────────────────────────────────────── */
  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const data = await api.getAllDummyPayments(params);
      const list = data.data || data.rows || (Array.isArray(data) ? data : []);
      setPayments(list);
      setStats({
        total: list.length,
        paid: list.filter(p => p.status === 'paid').length,
        refunded: list.filter(p => p.status === 'refunded').length,
        revenue: list.filter(p => p.status === 'paid').reduce((s, p) => s + (Number(p.amount) || 0), 0),
        activeSubs: 0,
      });
    } catch (e) {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [statusFilter]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const data = await api.getSubscriptionPlans();
      setPlans(data.data || data || []);
    } catch {
      setPlans([
        { id: 'basic_monthly', name: 'Basic Monthly', price: 299, currency: 'INR', billingCycle: 'monthly', durationDays: 30, features: ['1 Listing', 'Standard Support', 'Rap Map Access'], isActive: true },
        { id: 'pro_monthly', name: 'Pro Monthly', price: 699, currency: 'INR', billingCycle: 'monthly', durationDays: 30, features: ['5 Listings', 'Priority Support', 'Analytics Dashboard', 'Featured Listing'], isActive: true },
        { id: 'captain_quarterly', name: 'Captain Quarterly', price: 1799, currency: 'INR', billingCycle: 'quarterly', durationDays: 90, features: ['Unlimited Listings', 'Captain Badge', 'All Modules Access', 'Dedicated Support', 'Revenue Dashboard'], isActive: true },
        { id: 'enterprise_annual', name: 'Enterprise Annual', price: 5999, currency: 'INR', billingCycle: 'annual', durationDays: 365, features: ['Unlimited Everything', 'Custom Branding', 'API Access', 'Priority Onboarding', 'Analytics Export'], isActive: true },
      ]);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    setSubsLoading(true);
    try {
      const data = await api.getAllSubscriptions();
      const list = data.data || (Array.isArray(data) ? data : []);
      setSubscriptions(list);
      setStats(prev => ({ ...prev, activeSubs: list.filter(s => s.status === 'active').length }));
    } catch {
      setSubscriptions([]);
    } finally {
      setSubsLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { if (tab === 'ledger') loadPayments(); }, [tab, loadPayments]);
  useEffect(() => { if (tab === 'subscriptions') loadSubscriptions(); }, [tab, loadSubscriptions]);

  /* ── simulator ────────────────────────────────────────── */
  const handleSimulate = async () => {
    setSimLoading(true);
    setSimResult(null);
    setSimError('');
    try {
      // 1. Create order
      const orderRes = await api.createDummyOrder({
        amount: Number(simAmount),
        paymentFor: simFor,
        bookingId: simBookingId || undefined,
      });
      const orderId = orderRes.order_id;

      // 2. Simulate capture
      const payRes = await api.simulateDummyPayment({
        order_id: orderId,
        amount: Number(simAmount) * 100,
        bookingId: simBookingId || undefined,
      });

      setSimResult({ order_id: orderId, ...payRes });
      showTemporarySuccess(`💳 Dummy payment of ₹${simAmount} captured! ID: ${payRes.payment_id}`);
      loadPayments();
    } catch (e) {
      setSimError(e.message || 'Simulation failed');
    } finally {
      setSimLoading(false);
    }
  };

  /* ── refund ───────────────────────────────────────────── */
  const handleRefund = async () => {
    if (!refundPaymentId) return;
    setRefundLoading(true);
    try {
      await api.processDummyRefund({ payment_id: refundPaymentId, amount: Number(refundAmount) * 100 || 0, reason: refundReason });
      showTemporarySuccess(`↩ Refund processed for ${refundPaymentId}`);
      setRefundPaymentId('');
      setRefundAmount('');
      setRefundReason('');
      loadPayments();
    } catch (e) {
      alert(e.message);
    } finally {
      setRefundLoading(false);
    }
  };

  /* ── plan CRUD ────────────────────────────────────────── */
  const openPlanModal = (plan = null) => {
    setEditingPlan(plan);
    setPlanForm(plan ? {
      name: plan.name, price: String(plan.price), billingCycle: plan.billingCycle,
      durationDays: String(plan.durationDays), features: (plan.features || []).join('\n')
    } : { name: '', price: '', billingCycle: 'monthly', durationDays: '30', features: '' });
    setShowPlanModal(true);
  };

  const savePlan = async () => {
    const payload = {
      name: planForm.name,
      price: Number(planForm.price),
      billingCycle: planForm.billingCycle,
      durationDays: Number(planForm.durationDays),
      features: planForm.features.split('\n').map(f => f.trim()).filter(Boolean),
      isActive: true,
    };
    try {
      if (editingPlan) {
        await api.updateSubscriptionPlan(editingPlan.id, payload);
        showTemporarySuccess('Plan updated!');
      } else {
        await api.createSubscriptionPlan(payload);
        showTemporarySuccess('Plan created!');
      }
      setShowPlanModal(false);
      loadPlans();
    } catch (e) { alert(e.message); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this subscription plan?')) return;
    try { await api.deleteSubscriptionPlan(id); showTemporarySuccess('Plan deleted'); loadPlans(); } catch (e) { alert(e.message); }
  };

  const togglePlan = async (plan) => {
    try {
      await api.updateSubscriptionPlan(plan.id, { isActive: !plan.isActive, is_active: !plan.isActive });
      showTemporarySuccess(`Plan ${plan.isActive ? 'deactivated' : 'activated'}`);
      loadPlans();
    } catch (e) { alert(e.message); }
  };

  const cancelSub = async (id) => {
    if (!window.confirm('Cancel this subscription?')) return;
    try { await api.cancelSubscription(id); showTemporarySuccess('Subscription cancelled'); loadSubscriptions(); } catch (e) { alert(e.message); }
  };

  /* ── filtered payments ────────────────────────────────── */
  const filteredPayments = payments.filter(p => {
    const term = searchTerm.toLowerCase();
    return !term || (p.gateway_payment_id || '').toLowerCase().includes(term) ||
      (p.gateway_order_id || '').toLowerCase().includes(term) ||
      (p.user_email || '').toLowerCase().includes(term) ||
      (p.user_name || '').toLowerCase().includes(term);
  });

  /* ── TAB STYLES ───────────────────────────────────────── */
  const tabStyle = (id) => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
    background: tab === id ? 'var(--neon-purple)' : 'rgba(255,255,255,.06)',
    color: tab === id ? '#fff' : 'var(--text-secondary)',
    transition: 'all .2s',
  });

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* ── Header ── */}
      <div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <CreditCard size={28} color="var(--neon-purple)" /> Payments & Subscriptions
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 4 }}>
          Dummy payment gateway — zero real credentials required. Full transaction ledger, subscription plan management, and refund controls.
        </p>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16 }}>
        <StatCard icon={DollarSign}   label="Total Payments"   value={stats.total}  sub="All-time transactions"    color="#F59E0B" />
        <StatCard icon={CheckCircle}  label="Paid"             value={stats.paid}   sub="Captured payments"       color="#10B981" />
        <StatCard icon={ArrowUpRight} label="Platform Revenue" value={`₹${stats.revenue.toLocaleString()}`} sub="Dummy gateway" color="#8B5CF6" />
        <StatCard icon={RotateCcw}    label="Refunded"         value={stats.refunded} sub="Processed refunds"    color="#EF4444" />
        <StatCard icon={Users}        label="Active Subs"      value={stats.activeSubs} sub="Live subscriptions" color="#3B82F6" />
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={tabStyle('simulate')} onClick={() => setTab('simulate')}><Zap size={13} style={{ display: 'inline', marginRight: 5 }} />Simulate Payment</button>
        <button style={tabStyle('ledger')} onClick={() => { setTab('ledger'); loadPayments(); }}><Eye size={13} style={{ display: 'inline', marginRight: 5 }} />Transaction Ledger</button>
        <button style={tabStyle('refund')} onClick={() => setTab('refund')}><RotateCcw size={13} style={{ display: 'inline', marginRight: 5 }} />Refunds</button>
        <button style={tabStyle('plans')} onClick={() => setTab('plans')}><Package size={13} style={{ display: 'inline', marginRight: 5 }} />Subscription Plans</button>
        <button style={tabStyle('subscriptions')} onClick={() => { setTab('subscriptions'); loadSubscriptions(); }}><Crown size={13} style={{ display: 'inline', marginRight: 5 }} />Subscriptions</button>
      </div>

      {/* ════════════════════════════════════════════════════
          TAB: SIMULATE
      ════════════════════════════════════════════════════ */}
      {tab === 'simulate' && (
        <div className="cyber-card" style={{ padding: 28, background: 'linear-gradient(135deg,rgba(139,92,246,.05),rgba(15,23,42,.7))', borderColor: 'rgba(139,92,246,.35)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: '0.7rem', padding: '3px 10px', background: 'rgba(139,92,246,.2)', color: 'var(--neon-purple)', borderRadius: 6, fontWeight: 800 }}>
              <Sparkles size={10} style={{ display: 'inline', marginRight: 4 }} />DUMMY GATEWAY
            </span>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0 }}>Payment Simulator</h2>
          </div>

          {/* Info box */}
          <div style={{ background: 'rgba(0,0,0,.35)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 10, padding: '14px 18px', marginBottom: 22, fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            <strong style={{ color: '#F59E0B' }}>How it works:</strong> Click <em>Simulate Payment</em> → the backend creates a dummy order, instantly captures it with an HMAC-SHA256 signed payment ID, records the transaction in the database, and returns the full payload. No Razorpay credentials needed.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 18, marginBottom: 18 }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 700 }}>Amount (₹)</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  type="number" value={simAmount} onChange={e => setSimAmount(e.target.value)}
                  style={{ flex: 1, minWidth: 90, background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.9rem', fontWeight: 700 }}
                />
                {['99','299','499','1199','1799'].map(a => (
                  <button key={a} onClick={() => setSimAmount(a)}
                    style={{ padding: '7px 10px', borderRadius: 6, border: 'none', background: simAmount === a ? 'var(--neon-purple)' : 'rgba(255,255,255,.08)', color: simAmount === a ? '#fff' : 'var(--text-secondary)', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>
                    ₹{a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 700 }}>Payment For</label>
              <select value={simFor} onChange={e => setSimFor(e.target.value)}
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.85rem' }}>
                <option value="booking">Booking</option>
                <option value="subscription">Subscription</option>
                <option value="featured_room">Featured Room</option>
                <option value="standard">Standard Checkout</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 700 }}>Booking ID (optional)</label>
              <input value={simBookingId} onChange={e => setSimBookingId(e.target.value)} placeholder="e.g. booking UUID"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
          </div>

          <button onClick={handleSimulate} disabled={simLoading}
            style={{ padding: '12px 32px', borderRadius: 10, border: 'none', background: simLoading ? 'rgba(139,92,246,.5)' : 'var(--neon-purple)', color: '#fff', fontWeight: 800, fontSize: '0.95rem', cursor: simLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            {simLoading ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={16} />}
            {simLoading ? 'Processing...' : 'Simulate Payment Now'}
          </button>

          {simError && (
            <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: '0.83rem', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={15} /> {simError}
            </div>
          )}

          {simResult && (
            <div style={{ marginTop: 18, padding: '18px 20px', background: 'rgba(16,185,129,.07)', border: '1px solid rgba(16,185,129,.3)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <CheckCircle size={16} color="#10B981" />
                <span style={{ fontWeight: 800, color: '#10B981', fontSize: '0.9rem' }}>Payment Captured Successfully ✓</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
                {[
                  ['Payment ID', simResult.payment_id],
                  ['Order ID', simResult.order_id],
                  ['Amount', `₹${simAmount}`],
                  ['Status', simResult.status],
                  ['Gateway', 'Dummy Gateway'],
                  ['Signature', simResult.signature?.substring(0,24) + '...'],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: 'rgba(0,0,0,.3)', borderRadius: 8, padding: '10px 13px' }}>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>{k}</div>
                    <div style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {v}
                      {(k === 'Payment ID' || k === 'Order ID') && (
                        <button onClick={() => copyText(v, k)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0 }}>
                          {copied === k ? <Check size={11} color="#10B981" /> : <Copy size={11} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: LEDGER
      ════════════════════════════════════════════════════ */}
      {tab === 'ledger' && (
        <div className="cyber-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)', fontSize: '1.05rem' }}>Transaction Ledger</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search ID / user..."
                  style={{ paddingLeft: 32, paddingRight: 12, paddingTop: 7, paddingBottom: 7, background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', borderRadius: 7, color: '#fff', fontSize: '0.8rem', width: 180 }} />
              </div>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); }}
                style={{ background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 7, padding: '7px 10px', fontSize: '0.8rem' }}>
                <option value="">All Status</option>
                <option value="paid">Paid</option>
                <option value="created">Pending</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
              </select>
              <button onClick={loadPayments} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--border-color)', background: 'rgba(255,255,255,.06)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontWeight: 700 }}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {paymentsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /><br />Loading payments...
              </div>
            ) : filteredPayments.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                <CreditCard size={36} style={{ opacity: .3, marginBottom: 8 }} /><br />
                No payments found{payments.length === 0 ? ' — simulate one above!' : ''}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>PAYMENT ID</th><th>ORDER ID</th><th>USER</th><th>FOR</th>
                    <th>AMOUNT</th><th>GATEWAY</th><th>STATUS</th><th>DATE</th><th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((p, i) => (
                    <tr key={p.id || i}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--neon-cyan)' }}>
                        {p.gateway_payment_id?.substring(0, 20) || '—'}
                        {p.gateway_payment_id && <button onClick={() => copyText(p.gateway_payment_id, `pid_${i}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', marginLeft: 4 }}>
                          {copied === `pid_${i}` ? <Check size={10} color="#10B981" /> : <Copy size={10} />}
                        </button>}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{p.gateway_order_id?.substring(0, 20) || '—'}</td>
                      <td>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700 }}>{p.user_name || '—'}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{p.user_email || ''}</div>
                      </td>
                      <td><span style={{ fontSize: '0.72rem', padding: '2px 8px', background: 'rgba(139,92,246,.15)', color: 'var(--neon-purple)', borderRadius: 4, fontWeight: 700 }}>{p.payment_for || 'standard'}</span></td>
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>₹{(Number(p.amount) || 0).toLocaleString()}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--neon-amber)', fontWeight: 700 }}>{p.gateway?.toUpperCase() || 'DUMMY'}</td>
                      <td><StatusPill s={p.status} /></td>
                      <td style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        {p.status === 'paid' && (
                          <button onClick={() => { setRefundPaymentId(p.gateway_payment_id || ''); setRefundAmount(String(p.amount || '')); setTab('refund'); }}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(139,92,246,.4)', background: 'rgba(139,92,246,.1)', color: 'var(--neon-purple)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                            <RotateCcw size={11} style={{ display: 'inline', marginRight: 3 }} />Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: REFUND
      ════════════════════════════════════════════════════ */}
      {tab === 'refund' && (
        <div className="cyber-card" style={{ padding: 28, maxWidth: 560, borderColor: 'rgba(139,92,246,.35)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <RotateCcw size={20} color="var(--neon-purple)" />
            <h2 style={{ fontSize: '1.1rem', fontWeight: 900, margin: 0, color: 'var(--text-primary)' }}>Process Refund</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>Payment ID *</label>
              <input value={refundPaymentId} onChange={e => setRefundPaymentId(e.target.value)} placeholder="dpmt_xxxxxx"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '10px 13px', fontSize: '0.87rem', boxSizing: 'border-box', fontFamily: 'var(--font-mono)' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>Refund Amount (₹) — leave blank for full refund</label>
              <input value={refundAmount} onChange={e => setRefundAmount(e.target.value)} type="number" placeholder="0"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '10px 13px', fontSize: '0.87rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>Reason</label>
              <input value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="Admin initiated refund"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '10px 13px', fontSize: '0.87rem', boxSizing: 'border-box' }} />
            </div>
            <button onClick={handleRefund} disabled={refundLoading || !refundPaymentId}
              style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: refundLoading ? 'rgba(139,92,246,.5)' : 'var(--neon-purple)', color: '#fff', fontWeight: 800, fontSize: '0.9rem', cursor: !refundPaymentId || refundLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              {refundLoading ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RotateCcw size={16} />}
              Process Refund
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: SUBSCRIPTION PLANS
      ════════════════════════════════════════════════════ */}
      {tab === 'plans' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>Subscription Plans</h3>
            <button onClick={() => openPlanModal()}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--neon-purple)', color: '#fff', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={15} /> Add New Plan
            </button>
          </div>

          {plansLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading plans...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 18 }}>
              {plans.map(plan => (
                <PlanCard key={plan.id} plan={plan} onEdit={openPlanModal} onDelete={deletePlan} onToggle={togglePlan} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: SUBSCRIPTIONS
      ════════════════════════════════════════════════════ */}
      {tab === 'subscriptions' && (
        <div className="cyber-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>User Subscriptions</h3>
            <button onClick={loadSubscriptions} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--border-color)', background: 'rgba(255,255,255,.06)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem' }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {subsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading subscriptions...</div>
            ) : subscriptions.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Crown size={36} style={{ opacity: .3, marginBottom: 8 }} /><br />No subscriptions yet
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>USER</th><th>PLAN</th><th>AMOUNT</th><th>GATEWAY</th>
                    <th>STATUS</th><th>EXPIRES</th><th>CREATED</th><th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((s, i) => (
                    <tr key={s.id || i}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{s.user_name || '—'}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{s.user_email || ''}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{s.plan_name || s.plan_id}</div>
                      </td>
                      <td style={{ fontWeight: 800 }}>₹{(Number(s.amount) || 0).toLocaleString()}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--neon-amber)', fontWeight: 700 }}>{(s.gateway || 'DUMMY').toUpperCase()}</td>
                      <td><StatusPill s={s.status} /></td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{s.expires_at ? new Date(s.expires_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        {s.status === 'active' && (
                          <button onClick={() => cancelSub(s.id)}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,.4)', background: 'rgba(239,68,68,.1)', color: '#EF4444', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                            <XCircle size={11} style={{ display: 'inline', marginRight: 3 }} />Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          PLAN MODAL
      ════════════════════════════════════════════════════ */}
      {showPlanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="cyber-card" style={{ width: '100%', maxWidth: 480, padding: 28, position: 'relative' }}>
            <button onClick={() => setShowPlanModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <X size={18} />
            </button>
            <h3 style={{ margin: '0 0 20px', fontWeight: 900, color: 'var(--text-primary)' }}>
              {editingPlan ? 'Edit Plan' : 'Create Subscription Plan'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Plan Name *', key: 'name', type: 'text', placeholder: 'e.g. Pro Monthly' },
                { label: 'Price (₹) *', key: 'price', type: 'number', placeholder: '499' },
                { label: 'Duration (Days)', key: 'durationDays', type: 'number', placeholder: '30' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>{f.label}</label>
                  <input type={f.type} value={planForm[f.key]} placeholder={f.placeholder}
                    onChange={e => setPlanForm({ ...planForm, [f.key]: e.target.value })}
                    style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>Billing Cycle</label>
                <select value={planForm.billingCycle} onChange={e => setPlanForm({ ...planForm, billingCycle: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.87rem' }}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one_time">One Time</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 700 }}>Features (one per line)</label>
                <textarea value={planForm.features} onChange={e => setPlanForm({ ...planForm, features: e.target.value })}
                  placeholder="Unlimited Listings&#10;Priority Support&#10;Analytics Dashboard" rows={4}
                  style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-color)', color: '#fff', borderRadius: 8, padding: '9px 13px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={savePlan}
                  style={{ flex: 1, padding: '11px', borderRadius: 9, border: 'none', background: 'var(--neon-purple)', color: '#fff', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button onClick={() => setShowPlanModal(false)}
                  style={{ flex: 1, padding: '11px', borderRadius: 9, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
